/**
 * Descarga de un archivo de `/files` COMPROBANDO ANTES que el enlace responde (round 2026-10-28).
 *
 * Por qué existe. Las descargas con `?d=1` se hacían con un ancla temporal en la MISMA pestaña
 * (`<a href="…/files/<token>?d=1" download>`). En producción la página (`mialoja.cl`) y la API
 * (`api.mialoja.cl`) son orígenes distintos, así que el navegador IGNORA el atributo `download` y
 * el clic es una navegación. Si el servidor contesta con el archivo (`Content-Disposition:
 * attachment`) la página se queda donde está y baja el archivo; pero si contesta un error —el token
 * firmado venció (ventana de 6 h), el archivo ya no existe, la sesión cayó— la pestaña entera pasa a
 * mostrar el JSON del error. Es la SPA reemplazada, y en la app instalada del iPhone ni siquiera hay
 * botón «atrás».
 *
 * Qué hace ahora. Antes de dar el clic pide la MISMA URL con `HEAD` (sin cuerpo: no gasta datos ni
 * pasa el video por la memoria de la pestaña; el servidor responde las cabeceras y cuenta 0 bytes) y
 * con la sesión por cookie. Solo si contesta 200 se dispara la descarga. Cualquier otra respuesta se
 * traduce a un aviso en español y NO se navega:
 *
 *   401 → «El enlace venció. Recarga la página…»   403 → sin permiso   404 → «El archivo ya no existe.»
 *   otro error del servidor, o sin red / sin respuesta a tiempo → el aviso correspondiente.
 *
 * Un 401 de `/files` NO cierra la sesión (ver el interceptor de `services/apiClient.ts`): el aviso
 * es la reacción, y si la sesión de verdad cayó, la próxima llamada a `/api` la cierra con su 401.
 *
 * El nombre del archivo lo sigue poniendo el servidor en `Content-Disposition` (el ORIGINAL, firmado
 * en el token; regla 32 de docs/seguridad.md): el atributo `download` es solo una sugerencia para el
 * mismo origen. Queda una carrera de milisegundos (el token vence entre el HEAD y el GET) que ya no
 * justifica un iframe: es la misma que tiene cualquier descarga.
 *
 * Solo se comprueban los enlaces PROPIOS de `/files` (`clasificarUrlPropia(url) === "files"`, la misma
 * definición con que el interceptor de `apiClient` decide a qué URL le pone la cookie). Una URL ajena
 * —los archivos legacy de Cloudflare R2/Images que `attachUrls` deja pasar tal cual, avatares y entradas
 * sueltas que la migración del 2026-06-15 no cubrió— no se puede comprobar: su servidor no tiene CORS
 * para nuestra página, así que el `HEAD` lanzaría un TypeError que se leería como «sin red» y bloquearía
 * una descarga que antes funcionaba. Esas se dejan al navegador (ancla directa), como siempre.
 *
 * Las descargas en bloque (`downloadFile` / `downloadFilesAsZip`, utils/downloadHelpers.ts) hacen su
 * propio `fetch` y ya tratan el error: no pasan por aquí.
 */
import { message } from "antd";
import { clasificarUrlPropia } from "../services/apiClient";
import { getMsg } from "./antdStatic";

/** Qué respondió el servidor a la comprobación previa. */
export type EstadoEnlace = "ok" | "vencido" | "sin_permiso" | "inexistente" | "servidor" | "red";

/** Aviso (en español neutro) de cada motivo por el que NO se descarga. */
export const MENSAJES_DESCARGA: Record<Exclude<EstadoEnlace, "ok">, string> = {
  vencido: "El enlace venció. Recarga la página e inténtalo de nuevo.",
  sin_permiso: "No tienes permiso para descargar este archivo.",
  inexistente: "El archivo ya no existe.",
  servidor: "El servidor no pudo entregar el archivo. Inténtalo de nuevo en unos segundos.",
  red: "No se pudo comprobar el archivo. Revisa tu conexión e inténtalo de nuevo.",
};

/** Tiempo máximo de la comprobación (solo cabeceras: si tarda más, la red está mal). */
export const TIEMPO_COMPROBACION_MS = 15_000;

/** Traduce el código HTTP de la comprobación. 304 cuenta como «existe». */
export function estadoDeStatus(status: number): EstadoEnlace {
  if ((status >= 200 && status < 300) || status === 304) return "ok";
  if (status === 401) return "vencido";
  if (status === 403) return "sin_permiso";
  if (status === 404 || status === 410) return "inexistente";
  return "servidor";
}

/**
 * Pide SOLO las cabeceras de `url` (`HEAD`, con cookie, sin caché) y dice si se puede descargar.
 * Nunca lanza: un fallo de red o un tiempo agotado es `"red"`.
 */
export async function comprobarEnlace(
  url: string,
  timeoutMs: number = TIEMPO_COMPROBACION_MS,
): Promise<EstadoEnlace> {
  const control = typeof AbortController !== "undefined" ? new AbortController() : null;
  const reloj = control ? setTimeout(() => control.abort(), timeoutMs) : null;
  try {
    const res = await fetch(url, {
      method: "HEAD",
      credentials: "include",
      cache: "no-store",
      signal: control?.signal,
    });
    return estadoDeStatus(res.status);
  } catch {
    return "red";
  } finally {
    if (reloj) clearTimeout(reloj);
  }
}

/** Dispara la descarga con un ancla temporal en la misma pestaña (sin abrir nada nuevo). */
export function dispararDescarga(url: string, nombre?: string): void {
  const a = document.createElement("a");
  a.href = url;
  a.target = "_self";
  a.rel = "noopener";
  if (nombre) a.download = nombre;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/** Muestra el motivo de que no se descargara. Un solo aviso a la vez (no se apilan por clics repetidos). */
function avisarError(texto: string): void {
  (getMsg() ?? message).error({ content: texto, key: "descarga-archivo" });
}

/** Descargas en curso por URL: un doble clic comparte la comprobación en vez de bajar dos veces. */
const enCurso = new Map<string, Promise<boolean>>();

/**
 * Comprueba que `url` (ya con `?d=1`) responde y, solo entonces, la descarga. Devuelve `true` si se
 * disparó la descarga; `false` si no (y en ese caso YA se avisó el motivo: quien llama no repite el aviso).
 * Una URL que no es un `/files` propio no se comprueba (ver el encabezado): se descarga directo.
 */
export function descargarVerificado(url: string, nombre?: string): Promise<boolean> {
  if (clasificarUrlPropia(url) !== "files") {
    dispararDescarga(url, nombre);
    return Promise.resolve(true);
  }
  const previa = enCurso.get(url);
  if (previa) return previa;
  const trabajo = (async () => {
    const estado = await comprobarEnlace(url);
    if (estado !== "ok") {
      avisarError(MENSAJES_DESCARGA[estado]);
      return false;
    }
    dispararDescarga(url, nombre);
    return true;
  })().finally(() => {
    enCurso.delete(url);
  });
  enCurso.set(url, trabajo);
  return trabajo;
}
